/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { SxProps } from '@mui/material';
import { FormControl, MenuItem, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import { t } from 'i18next';
import React from 'react';
import { TooltipSelect } from '@opengeoweb/shared';

export interface WarningListSelectOption {
  value: string;
  label: string | ((t: TFunction) => string);
}

export interface WarningListSelectProps {
  options: WarningListSelectOption[];
  label: string;
  value?: string;
  onChange: (value: string) => void;
  renderOption?: (
    option: WarningListSelectOption,
    index: number,
  ) => React.ReactElement;
  renderValue?: (option: unknown) => React.ReactNode;
  gridArea: string;
  additionalStyling?: SxProps;
  isReadOnly?: boolean;
  emptyMessage?: string;
  validationResult?: true | 'error' | 'warning';
}

export const WarningListSelect = React.memo(
  ({
    options,
    value,
    label,
    onChange,
    renderOption,
    renderValue,
    gridArea,
    additionalStyling,
    isReadOnly = false,
    emptyMessage,
    validationResult = true,
  }: WarningListSelectProps) => {
    const currentIndex = options.findIndex((option) => option.value === value);
    const hasEmptyMessage =
      emptyMessage !== undefined && emptyMessage.length > 0;

    return (
      <FormControl style={{ width: '100%', gridArea }}>
        <TooltipSelect
          SelectDisplayProps={{ 'aria-label': label }}
          additionalStyling={{
            '&.Mui-disabled .MuiSvgIcon-root': {
              display: 'none',
            },
            '&.Mui-disabled': {
              color: 'inherit',
              '& .MuiInput-input': {
                WebkitTextFillColor: 'inherit',
              },
            },
            ...(validationResult !== true && {
              backgroundColor: `geowebColors.functional.${validationResult}Outline.fill`,
            }),
            ...(Array.isArray(additionalStyling)
              ? Object.assign({}, ...additionalStyling)
              : additionalStyling),
          }}
          currentIndex={currentIndex > -1 ? currentIndex : undefined}
          disableUnderline
          disabled={isReadOnly}
          displayEmpty={hasEmptyMessage}
          isEnabled={!isReadOnly}
          list={options}
          renderValue={renderValue}
          style={{ maxWidth: '100%' }}
          tooltip=""
          value={value ?? ''}
          {...(hasEmptyMessage && {
            renderValue: (value): React.ReactNode => {
              if (value === '' && hasEmptyMessage) {
                return (
                  <Typography
                    color="textSecondary"
                    sx={{
                      alignSelf: 'center',
                    }}
                    variant="caption"
                  >
                    {emptyMessage}
                  </Typography>
                );
              }

              if (renderValue) {
                return renderValue(value);
              }

              const option = options.find((item) => item.value === value);
              return typeof option?.label === 'function'
                ? option.label(t)
                : option?.label;
            },
          })}
          onChange={(event): void => {
            onChange(event.target.value as string);
          }}
          onChangeMouseWheel={(event): void => {
            onChange(event.value);
          }}
        >
          <MenuItem disabled sx={{ display: 'none' }} />
          {options.map((option, index) => {
            if (renderOption) {
              return renderOption(option, index);
            }

            return (
              <MenuItem
                key={option.value}
                sx={(theme) => ({
                  '&.MuiMenuItem-root': {
                    ...theme.typography.caption,
                  },
                })}
                value={option.value}
              >
                {typeof option.label === 'function'
                  ? option.label(t)
                  : option.label}
              </MenuItem>
            );
          })}
        </TooltipSelect>
      </FormControl>
    );
  },
);
